import React, { useCallback, useState } from 'react';
import Card from '@applied/dragonfruit/src/components/Card/Card';
import Flex from '@applied/dragonfruit/src/components/Layout/Flex';
import ConditionalLink from '@applied/dragonfruit/src/components/Link/ConditionalLink';
import { Typography } from '@applied/dragonfruit/src/components/Text/Typography';
import { Currency, formatSubdivisions } from 'api/compensation';

import {
  DULL_BLUE,
  hslToString,
  interpolateHSLColor,
  SATURATED_BLUE,
} from 'components/common/color';
import { Divider } from 'components/common/Divider/Divider';
import {
  mapRangeToSliderValue,
  mapSliderValueToRange,
  Slider,
} from 'components/common/Slider/Slider';
import { ValuationTick } from 'components/WebOfferPage/EquitySection/ValuationTick/ValuationTick';
import { useWebOffer } from 'components/WebOfferPage/web_offer_context';

import './valuation-slider.scss';

export const MIN_VALUATION_MULTIPLIER = 1;
const MAX_VALUATION_MULTIPLIER = 10;
const MULTIPLIER_INCREMENT = 0.1;
const VALUATION_TICK_MULTIPLIERS = [1, 3, MAX_VALUATION_MULTIPLIER];
const EQUITY_PAGE_URL =
  'https://applied-8a3f62-a8bc2a055f-55714dd803e2d.webflow.io/128412084b97106b33375927';

type ValuationSliderProps = {
  currentValuation: number;
};

export function ValuationSlider(props: ValuationSliderProps): JSX.Element {
  const { currentValuation } = props;
  const { equityUsdCents } = useWebOffer();

  const [multiplier, setMultiplier] = useState(MIN_VALUATION_MULTIPLIER);
  const updateMultiplier = useCallback((value: number): void => {
    setMultiplier(
      mapSliderValueToRange(value, MIN_VALUATION_MULTIPLIER, MAX_VALUATION_MULTIPLIER, 1),
    );
  }, []);

  const sliderValue = mapRangeToSliderValue(
    multiplier,
    MIN_VALUATION_MULTIPLIER,
    MAX_VALUATION_MULTIPLIER,
  );
  const sliderColor = hslToString(interpolateHSLColor(DULL_BLUE, SATURATED_BLUE, sliderValue));

  return (
    <Flex className="valuation-slider" direction="column" gap="lg">
      <Flex className="slider-container" direction="column" gap="md">
        <Typography variant="subtitle">Hypothetical Future Valuations</Typography>
        <Slider
          value={sliderValue}
          minIncrement={mapRangeToSliderValue(
            Math.ceil(MIN_VALUATION_MULTIPLIER / MULTIPLIER_INCREMENT) * MULTIPLIER_INCREMENT,
            MIN_VALUATION_MULTIPLIER,
            MAX_VALUATION_MULTIPLIER,
          )}
          maxIncrement={mapRangeToSliderValue(
            Math.floor(MAX_VALUATION_MULTIPLIER / MULTIPLIER_INCREMENT) * MULTIPLIER_INCREMENT,
            MIN_VALUATION_MULTIPLIER,
            MAX_VALUATION_MULTIPLIER,
          )}
          increment={MULTIPLIER_INCREMENT / (MAX_VALUATION_MULTIPLIER - MIN_VALUATION_MULTIPLIER)}
          setValue={updateMultiplier}
          editable
          color={sliderColor}
          handleElement={
            <div className="multiplier-pill" style={{ backgroundColor: sliderColor }}>
              <Typography variant="body">{multiplier}x</Typography>
            </div>
          }
        />
        <div className="valuation-slider-tick-container">
          {VALUATION_TICK_MULTIPLIERS.map((tickMultiplier) => (
            <ValuationTick
              key={tickMultiplier}
              currentValuation={currentValuation}
              multiplier={tickMultiplier}
              minMultiplier={MIN_VALUATION_MULTIPLIER}
              maxMultiplier={MAX_VALUATION_MULTIPLIER}
            />
          ))}
        </div>
      </Flex>
      <Card className="valuation-slider-card" direction="column" padding="none">
        <Flex className="total-value" direction="row">
          <Flex
            className="valuation-slider-card-header"
            direction="column"
            alignment="start"
            justify="center"
            padding="lg"
            grow
          >
            <Typography variant="header">Total Equity Value (RSUs)</Typography>
          </Flex>
          <Flex direction="column" alignment="center" justify="center" padding="lg" grow>
            <Typography variant="body">
              {formatSubdivisions(equityUsdCents * multiplier, Currency.USD)}
            </Typography>
          </Flex>
        </Flex>
        <Divider orientation="horizontal" />
        <Flex className="total-value" direction="row">
          <Flex
            className="valuation-slider-card-header"
            direction="column"
            alignment="start"
            justify="center"
            padding="lg"
            grow
          >
            <Typography variant="header">Approx. Annual Equity Value</Typography>
          </Flex>
          <Flex direction="column" alignment="center" justify="center" padding="lg" grow>
            <Typography variant="body">
              {formatSubdivisions((equityUsdCents * multiplier) / 4, Currency.USD)}
            </Typography>
          </Flex>
        </Flex>
      </Card>
      <Typography className="equity-page-link" variant="caption">
        Check out our <ConditionalLink link={EQUITY_PAGE_URL}>equity page</ConditionalLink>{' '}
        (Password: applied123) to learn more
      </Typography>
    </Flex>
  );
}
